import React, { useMemo } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import {
  ChecklistFormShell,
  Section,
  Field,
  useChecklistForm,
  inputClass,
  labelClass,
} from './ChecklistFormShared';
import { buildChecklistDefaultValues } from '../../portal/utils/annualReportChecklistConfig';

const emptyDirectorItem = (fields) => {
  const item = {};
  (fields || []).forEach((f) => {
    item[f.key] = '';
  });
  return item;
};

const CmsChecklistForm = ({ checklist, shell, initialData, onSave, onCancel, isSubmitting }) => {
  const defaultValues = useMemo(() => buildChecklistDefaultValues(checklist), [checklist]);
  const mergedInitial = useMemo(() => {
    const merged = { ...defaultValues, ...(initialData || {}) };
    (checklist?.sections || []).forEach((section) => {
      if (section.type === 'directorList' && section.key) {
        merged[section.key] = initialData?.[section.key]?.length
          ? initialData[section.key]
          : defaultValues[section.key];
      }
    });
    return merged;
  }, [checklist, defaultValues, initialData]);
  const [formData, setFormData, updateField] = useChecklistForm(mergedInitial, defaultValues);

  const sections = (checklist?.sections || [])
    .filter((section) => section.isActive !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const updateDirector = (listKey, index, fieldKey, value) => {
    setFormData((prev) => {
      const list = [...(prev[listKey] || [])];
      list[index] = { ...list[index], [fieldKey]: value };
      return { ...prev, [listKey]: list };
    });
  };

  const addDirector = (listKey, fields) => {
    setFormData((prev) => ({
      ...prev,
      [listKey]: [...(prev[listKey] || []), emptyDirectorItem(fields)],
    }));
  };

  const removeDirector = (listKey, index, minItems = 1) => {
    setFormData((prev) => {
      const list = prev[listKey] || [];
      if (list.length <= minItems) return prev;
      return { ...prev, [listKey]: list.filter((_, i) => i !== index) };
    });
  };

  const renderFields = (fields, getValue, setValue) =>
    (fields || [])
      .filter((f) => f.isActive !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((f) => {
        const wrapperClass = f.fullWidth ? 'md:col-span-2' : '';
        return (
          <div key={f.key} className={wrapperClass}>
            <Field
              label={f.label}
              value={getValue(f.key)}
              onChange={(v) => setValue(f.key, v)}
              type={f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : 'text'}
              multiline={f.multiline}
              required={f.required}
            />
          </div>
        );
      });

  return (
    <ChecklistFormShell
      title={checklist?.title}
      subtitle={checklist?.subtitle}
      backLabel={shell?.backLabel}
      saveDraftLabel={shell?.saveDraftLabel}
      submitLabel={shell?.submitLabel}
      onCancel={onCancel}
      onSaveDraft={() => onSave(formData, false)}
      onSubmit={() => onSave(formData, true)}
      isSubmitting={isSubmitting}
    >
      {sections.map((section, sectionIndex) => {
        if (section.type === 'fields') {
          return (
            <Section key={`fields-${sectionIndex}`} title={section.title}>
              {renderFields(section.fields, (key) => formData[key], (key, value) => updateField(key, value))}
            </Section>
          );
        }

        if (section.type === 'select') {
          return (
            <Section key={`select-${sectionIndex}`} title={section.title}>
              <div className="md:col-span-2">
                <label className={labelClass}>{section.label}</label>
                <select
                  className={inputClass}
                  value={formData[section.key] || ''}
                  onChange={(e) => updateField(section.key, e.target.value)}
                >
                  {(section.options || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </Section>
          );
        }

        if (section.type === 'directorList') {
          const list = formData[section.key] || [];
          return (
            <div key={`directors-${sectionIndex}`} className="space-y-3">
              <div className="flex items-center justify-between gap-2 border-b border-gray-100 dark:border-dark-700 pb-2">
                <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200">{section.title}</h3>
                <button
                  type="button"
                  onClick={() => addDirector(section.key, section.fields)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600"
                >
                  <FaPlus className="w-3 h-3" /> {section.addLabel || 'Add Director'}
                </button>
              </div>
              {list.map((director, index) => (
                <div key={index} className="p-4 rounded-xl border border-gray-100 dark:border-dark-700 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">
                      {section.itemLabelPrefix || 'Director'} {index + 1}
                    </span>
                    {list.length > (section.minItems || 1) && (
                      <button
                        type="button"
                        onClick={() => removeDirector(section.key, index, section.minItems)}
                        className="text-red-500"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {renderFields(
                      section.fields,
                      (key) => director[key],
                      (key, value) => updateDirector(section.key, index, key, value)
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        }

        return null;
      })}
    </ChecklistFormShell>
  );
};

export default CmsChecklistForm;
